import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEventHandler,
  type MutableRefObject,
  type RefObject,
} from "react";
import {
  ChatComposerModeSwitcher,
  ChatComposerSendStopButton,
  CloseIcon,
  ComposerAttachmentStrip as SharedComposerAttachmentStrip,
  getChatComposerModeChrome,
  type ComposerAttachment,
} from "@sudowrite/shared-chat";
import { AppEventPublisher, AgenticChatMode } from "src/AppEventPublisher";
import { cn } from "src/lib/cn";
import type { PlanData } from "src/stores/persistentChatStore";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { composerChromeButtonClassName } from "src/lib/composerChrome";
import * as SelectPrimitive from "@radix-ui/react-select";
import type { ChatComposerModeLabelVariant } from "@sudowrite/shared-chat";
import type { AttachmentDoc } from "./PersistentChatContext";
import { MentionTiptapEditor, type MentionTiptapEditorHandle } from "./mentions/MentionTiptapEditor";
import type { CTAAction } from "./ThinkingStep/cta-types";

type AdminBrainModelOption = { label: string; value: string };

const pasteRegex = /~~pasted~~\[(.*?)\]\((.*?)\)/g;
const mentionRegex = /(?:@|#)\[.*?\]\((character|worldbuilding|document)::/g;

export type ChatComposerHandle = {
  focus: () => void;
  clear: () => void;
  openMentionPicker: () => void;
  removeAttachment: (id: string) => void;
};

type Props = {
  agentIsRunning: boolean;
  sendQueued?: boolean;
  activePlan: PlanData | null;
  ctaAction: CTAAction | null;
  agenticChatMode: string;
  modeLabel: string;
  onAgenticChatModeChange: (mode: string) => void;
  /** Admin-only brain model picker. Omit or leave isAdmin false to hide. */
  isAdmin?: boolean;
  agenticChatBrainModel?: string;
  adminBrainModelOptions?: AdminBrainModelOption[];
  onAgenticChatBrainModelChange?: (modelKey: string) => void;
  modeLabelVariant: ChatComposerModeLabelVariant;
  /** When false, the composer shows Allow edits only (no Discuss mode switcher). */
  discussModeAvailable?: boolean;
  onSend: (
    message: string,
    meta: { hasAttachments: boolean; hasMentions: boolean; pastedExcerpts: string[] },
  ) => void;
  onStopAgent: () => void;
  onAttachmentsChange?: (docs: AttachmentDoc[]) => void;
  onComposerKeyDownCapture?: KeyboardEventHandler<HTMLDivElement>;
  /** When focus leaves the composer but moves to this element, do not treat as blur (passage chip). */
  activeHighlightChipRef?: RefObject<HTMLElement | null>;
  containerRef?: RefObject<HTMLDivElement | null>;
  /** Fired when the Allow edits / Chat only menu opens or closes (for parent z-index stacking). */
  onModeMenuOpenChange?: (open: boolean) => void;
};

function stripPasteMarkersForAttachmentId(markup: string, id: string): string {
  let result = markup;
  const mentions = markup.matchAll(pasteRegex);
  mentions?.forEach?.((match) => {
    const [original, , pasteId] = match;
    if (pasteId === id) {
      result = result.replace(original, "");
    }
  });
  return result;
}

/** Renders above the composer in the column-reverse footer (must be a late sibling in PersistentChatUI). */
export function ComposerAttachmentStrip({
  docs,
  onRemove,
}: {
  docs: AttachmentDoc[];
  onRemove: (doc: AttachmentDoc) => void;
}) {
  if (!docs.length) return null;

  return (
    <WithReset className="pb-2">
      <SharedComposerAttachmentStrip
        docs={docs.map<ComposerAttachment>((doc) => ({
          id: doc.id,
          wordCount: doc.wordCount,
          type: doc.type,
          label: "Pasted",
        }))}
        onRemove={(doc) => {
          const matched = docs.find((attachment) => attachment.id === doc.id);
          if (matched) {
            onRemove(matched);
          }
        }}
        icons={{ close: <CloseIcon /> }}
      />
    </WithReset>
  );
}

/**
 * Isolated chat composer so keystrokes do not re-render PersistentChatUI / the message list.
 * Draft text lives here until send; attachment chips render in the parent for column-reverse layout.
 */
export const ChatComposer = memo(
  forwardRef<ChatComposerHandle, Props>(function ChatComposer(
    {
      agentIsRunning,
      sendQueued = false,
      activePlan,
      ctaAction,
      agenticChatMode,
      modeLabel,
      onAgenticChatModeChange,
      isAdmin = false,
      agenticChatBrainModel,
      adminBrainModelOptions = [],
      onAgenticChatBrainModelChange,
      modeLabelVariant,
      discussModeAvailable = true,
      onSend,
      onStopAgent,
      onAttachmentsChange,
      onComposerKeyDownCapture,
      activeHighlightChipRef,
      containerRef,
      onModeMenuOpenChange,
    },
    ref,
  ) {
    const [instructions, setInstructions] = useState("");
    const [attachmentDocs, setAttachmentDocs] = useState<AttachmentDoc[]>([]);
    const [modeMenuOpen, setModeMenuOpen] = useState(false);

    const handleModeMenuOpenChange = useCallback(
      (open: boolean) => {
        setModeMenuOpen(open);
        onModeMenuOpenChange?.(open);
      },
      [onModeMenuOpenChange],
    );
    const editorRef = useRef<MentionTiptapEditorHandle>(null);
    const innerContainerRef = useRef<HTMLDivElement>(null);
    const modeSwitcherRef = useRef<HTMLDivElement>(null);
    const modeMenuOpenRef = useRef(modeMenuOpen);
    const pendingModeMenuDismissRef = useRef<number | null>(null);

    modeMenuOpenRef.current = modeMenuOpen;
    const didSyncAttachmentsToParentRef = useRef(false);

    useEffect(() => {
      // Skip the initial empty sync so mount-time store updates in the parent
      // (e.g. chat switch clearing currentUserQuestion) are not immediately
      // painted away by an extra parent re-render.
      if (!didSyncAttachmentsToParentRef.current && attachmentDocs.length === 0) {
        return;
      }
      didSyncAttachmentsToParentRef.current = true;
      onAttachmentsChange?.(attachmentDocs);
    }, [attachmentDocs, onAttachmentsChange]);

    const removeAttachmentById = useCallback((id: string) => {
      setAttachmentDocs((prev) => prev.filter((doc) => doc.id !== id));
      setInstructions((current) => stripPasteMarkersForAttachmentId(current, id));
      editorRef.current?.focus();
    }, []);

    useImperativeHandle(ref, () => ({
      focus: () => editorRef.current?.focus(),
      clear: () => editorRef.current?.clear(),
      openMentionPicker: () => editorRef.current?.openMentionPicker(),
      removeAttachment: removeAttachmentById,
    }), [removeAttachmentById]);

    const setContainerRef = useCallback(
      (node: HTMLDivElement | null) => {
        innerContainerRef.current = node;
        if (containerRef) {
          (containerRef as MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [containerRef],
    );

    useEffect(() => {
      const el = innerContainerRef.current;
      if (!el) return;
      const onFocusOut = (e: FocusEvent) => {
        if (!el.contains(e.relatedTarget as Node | null)) {
          setTimeout(() => {
            const activeElement = document.activeElement;
            const focusMovedToHighlightChip = !!(
              activeElement &&
              activeHighlightChipRef?.current?.contains(activeElement)
            );
            if (!el.contains(activeElement) && !focusMovedToHighlightChip) {
              // Reserved for future composer-focus UI; intentionally no parent state update.
            }
          }, 0);
        }
      };
      el.addEventListener("focusout", onFocusOut);
      return () => el.removeEventListener("focusout", onFocusOut);
    }, [activeHighlightChipRef]);

    useEffect(() => {
      return () => {
        onModeMenuOpenChange?.(false);
      };
    }, [onModeMenuOpenChange]);

    useEffect(() => {
      if (!modeMenuOpen) {
        if (pendingModeMenuDismissRef.current !== null) {
          cancelAnimationFrame(pendingModeMenuDismissRef.current);
          pendingModeMenuDismissRef.current = null;
        }
        return;
      }

      const isInsideModeMenu = (target: EventTarget | null) => {
        if (!(target instanceof Node)) return false;
        if (modeSwitcherRef.current?.contains(target)) return true;
        return target instanceof Element && !!target.closest("[data-chat-composer-mode-menu]");
      };
      // Defer outside-dismiss to the next frame so Tamagui menu onPress runs first.
      const onDocumentPointerDown = (event: PointerEvent) => {
        if (isInsideModeMenu(event.target)) return;
        if (pendingModeMenuDismissRef.current !== null) {
          cancelAnimationFrame(pendingModeMenuDismissRef.current);
        }
        const pointerTarget = event.target;
        pendingModeMenuDismissRef.current = window.requestAnimationFrame(() => {
          pendingModeMenuDismissRef.current = null;
          if (!modeMenuOpenRef.current) return;
          if (isInsideModeMenu(pointerTarget)) return;
          handleModeMenuOpenChange(false);
        });
      };
      document.addEventListener("pointerdown", onDocumentPointerDown);
      return () => {
        if (pendingModeMenuDismissRef.current !== null) {
          cancelAnimationFrame(pendingModeMenuDismissRef.current);
          pendingModeMenuDismissRef.current = null;
        }
        document.removeEventListener("pointerdown", onDocumentPointerDown);
      };
    }, [handleModeMenuOpenChange, modeMenuOpen]);

    const plainInstructionsForShortcuts = useMemo(
      () =>
        instructions
          .replace(/(?:@|#)\[([^\]]+)\]\([^)]+\)/g, "$1")
          .replace(/~~pasted~~\[[^\]]+\]\([^)]+\)/g, "")
          .trim(),
      [instructions],
    );

    const canSend = useMemo(() => {
      const planBlocking = activePlan && (activePlan.status === "in_progress" || activePlan.status === "cancelling");
      const ctaBlocking = ctaAction && ctaAction.status === "awaiting_input";
      return (instructions.trim().length > 0 || attachmentDocs.length > 0) && !agentIsRunning && !sendQueued && !planBlocking && !ctaBlocking;
    }, [attachmentDocs.length, instructions, agentIsRunning, sendQueued, activePlan, ctaAction]);

    const { options: allModeOptions, switcherIcons: sharedComposerIcons } = useMemo(
      () => getChatComposerModeChrome(modeLabelVariant),
      [modeLabelVariant],
    );
    const modeOptions = useMemo(
      () => (discussModeAvailable ? allModeOptions : allModeOptions.filter((option) => option.mode === "write")),
      [allModeOptions, discussModeAvailable],
    );

    const brainModelSelectOptions = useMemo(
      () => adminBrainModelOptions.map((option) => ({ value: option.value, label: option.label })),
      [adminBrainModelOptions],
    );

    const selectedBrainModelLabel = useMemo(() => {
      const match = adminBrainModelOptions.find((option) => option.value === agenticChatBrainModel);
      return match?.label ?? agenticChatBrainModel ?? "Brain";
    }, [adminBrainModelOptions, agenticChatBrainModel]);

    const handlePaste = useCallback((text: string, id: string, wordCount: number) => {
      setAttachmentDocs((prev) => {
        if (prev.find((doc) => doc.type === "paste" && doc.content === text)) {
          return prev;
        }
        AppEventPublisher.agenticChat.pasteAttachment(wordCount);
        return [...prev, { type: "paste", content: text, wordCount, id }];
      });
    }, []);

    const handleSubmit = useCallback(() => {
      let base = instructions.trim();
      editorRef.current?.clear();
      setInstructions("");

      const mentionMatches = base.match(mentionRegex) || [];
      const hasMentions = mentionMatches.length > 0;

      const pastedMentions = base.matchAll(pasteRegex);
      const matchedAttachments: string[] = [];

      pastedMentions?.forEach?.((match) => {
        const [original, , id] = match;
        const pasteData = attachmentDocs.find((doc) => doc.id === id);
        if (!pasteData) {
          base = base.replace(original, ``);
          return;
        }
        matchedAttachments.push(pasteData.id);
        base = base.replace(original, `${pasteData.content}`);
      });

      const attacmentsNotMentioned = attachmentDocs.filter((doc) => !matchedAttachments.includes(doc.id));
      attacmentsNotMentioned?.forEach((doc) => {
        base += `\n\n${doc.content}`;
      });

      onSend(base, {
        hasAttachments: attachmentDocs.length > 0,
        hasMentions,
        pastedExcerpts: attachmentDocs.map((doc) => doc.content),
      });

      setAttachmentDocs([]);
    }, [attachmentDocs, instructions, onSend]);

    const handleSubmitShortcutStop = useCallback(() => {
      onStopAgent();
      editorRef.current?.clear();
      setInstructions("");
    }, [onStopAgent]);

    return (
      <div ref={setContainerRef}>
        <MentionTiptapEditor
          ref={editorRef}
          value={instructions}
          onChange={setInstructions}
          onSubmit={handleSubmit}
          plainTextForShortcuts={plainInstructionsForShortcuts}
          onSubmitShortcutOverride={agentIsRunning ? handleSubmitShortcutStop : undefined}
          onKeyDownCapture={onComposerKeyDownCapture}
          onPasteLongText={handlePaste}
          canSubmit={canSend}
          placeholder="Ask a question about your project"
          bottomControls={
            <>
              <WithReset className="flex items-center gap-1">
                <div ref={modeSwitcherRef}>
                  {discussModeAvailable ? (
                    <ChatComposerModeSwitcher
                      currentMode={agenticChatMode}
                      modeLabel={modeLabel}
                      options={modeOptions}
                      menuPlacement="up"
                      menuZIndex={2147483647}
                      open={modeMenuOpen}
                      onOpenChange={handleModeMenuOpenChange}
                      onModeChange={(nextMode) => {
                        const nextModeValue = `${nextMode}`;
                        if (nextModeValue !== agenticChatMode) {
                          const fromMode = agenticChatMode === "write" ? AgenticChatMode.Write : AgenticChatMode.Chat;
                          const toMode = nextModeValue === "write" ? AgenticChatMode.Write : AgenticChatMode.Chat;
                          AppEventPublisher.agenticChat.modeSwitch(fromMode, toMode);
                        }
                        onAgenticChatModeChange(nextModeValue);
                      }}
                      icons={sharedComposerIcons}
                    />
                  ) : (
                    <div
                      className={cn(composerChromeButtonClassName, "cursor-default")}
                      aria-label={modeLabel}
                    >
                      {sharedComposerIcons.pencil ? (
                        <span className="inline-flex h-4 w-4 items-center justify-center [&_svg]:h-4 [&_svg]:w-4">
                          {sharedComposerIcons.pencil}
                        </span>
                      ) : null}
                      <span>{modeLabel}</span>
                    </div>
                  )}
                </div>
                {isAdmin && brainModelSelectOptions.length > 0 && onAgenticChatBrainModelChange ? (
                  <SelectPrimitive.Root
                    value={agenticChatBrainModel}
                    onValueChange={onAgenticChatBrainModelChange}
                  >
                    <SelectPrimitive.Trigger
                      className={cn(composerChromeButtonClassName, "outline-none")}
                      aria-label="Agent brain model"
                    >
                      <SelectPrimitive.Value placeholder="Brain">
                        {selectedBrainModelLabel}
                      </SelectPrimitive.Value>
                    </SelectPrimitive.Trigger>
                    <SelectPrimitive.Portal>
                      <SelectPrimitive.Content
                        position="popper"
                        side="top"
                        align="start"
                        sideOffset={4}
                        className="z-[2147483647] origin-bottom animate-flyout bg-app-menu-color shadow-card border border-app-menu-color-hover p-1 rounded-xl"
                      >
                        <SelectPrimitive.Viewport>
                          {brainModelSelectOptions.map((option) => (
                            <SelectPrimitive.Item
                              key={option.value}
                              value={option.value}
                              className={cn(
                                "rounded-[10px] px-3 py-2 text-sm outline-none cursor-pointer",
                                "text-app-grey-0 data-[highlighted]:bg-app-menu-color-hover data-[state=checked]:font-semibold",
                              )}
                            >
                              <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                            </SelectPrimitive.Item>
                          ))}
                        </SelectPrimitive.Viewport>
                      </SelectPrimitive.Content>
                    </SelectPrimitive.Portal>
                  </SelectPrimitive.Root>
                ) : null}
                <WithReset
                  as="button"
                  type="button"
                  className={cn(composerChromeButtonClassName, "min-w-[28px] text-sm font-semibold")}
                  aria-label="Open mention picker"
                  title="Insert mention (#)"
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onClick={() => editorRef.current?.openMentionPicker()}
                >
                  #
                </WithReset>
              </WithReset>
              <WithReset className="flex items-center ml-auto">
                <ChatComposerSendStopButton
                  agentIsRunning={agentIsRunning}
                  canSend={canSend}
                  onSend={handleSubmit}
                  onStop={onStopAgent}
                  icons={sharedComposerIcons}
                />
              </WithReset>
            </>
          }
        />
      </div>
    );
  }),
);

ChatComposer.displayName = "ChatComposer";
